import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ApiService from '../../services/api';
import { useRegistration } from '../../context/RegistrationContext';
import { passwordPolicySummary, validatePassword } from '../../utils/passwordPolicy';
import { normalizePhoneInput } from '../../utils/normalizePhoneInput';

export const UserAccountPanel: React.FC = () => {
  const { registrationData } = useRegistration();
  const [userEmail, setUserEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const res = await ApiService.getCurrentUser();
      const user = res?.data?.user || res?.user;
      if (user?.email) {
        setUserEmail(String(user.email));
      } else if (registrationData?.email) {
        setUserEmail(String(registrationData.email));
      }
      const profile = user?.profile;
      if (profile) {
        setFirstName(String(profile.firstName || ''));
        setLastName(String(profile.lastName || ''));
        setPhone(normalizePhoneInput(profile.phone));
      }
    } catch {
      /* use registration context fallback */
      if (registrationData?.email) {
        setUserEmail(String(registrationData.email));
      }
      setPhone('');
    } finally {
      setProfileLoading(false);
    }
  }, [registrationData?.email]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileError(null);
    setProfileMessage(null);
    try {
      await ApiService.updateUserProfile({ firstName, lastName, phone: normalizePhoneInput(phone) });
      setProfileMessage('Profile updated.');
      await loadProfile();
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordMessage(null);
    const passwordCheck = validatePassword(newPassword);
    if (!passwordCheck.valid) {
      setPasswordError(passwordCheck.message || passwordPolicySummary());
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    try {
      await ApiService.changePassword({ currentPassword, newPassword });
      setPasswordMessage('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          My account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {profileLoading && !userEmail && !registrationData?.email
            ? 'Signed in'
            : (
              <>
                Signed in as{' '}
                <strong>{userEmail || registrationData?.email || 'Unavailable'}</strong>
              </>
            )}
        </Typography>
        {profileMessage && <Alert severity="success" sx={{ mb: 1 }}>{profileMessage}</Alert>}
        {profileError && <Alert severity="error" sx={{ mb: 1 }}>{profileError}</Alert>}
        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
          <TextField
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            disabled={profileLoading}
          />
          <TextField
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            disabled={profileLoading}
          />
        </Stack>
        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(normalizePhoneInput(e.target.value))}
          fullWidth
          sx={{ mt: 2 }}
          disabled={profileLoading}
        />
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleSaveProfile}
          disabled={profileSaving || profileLoading}
        >
          {profileSaving ? <CircularProgress size={22} color="inherit" /> : 'Save account details'}
        </Button>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Change password
        </Typography>
        {passwordMessage && <Alert severity="success" sx={{ mb: 1 }}>{passwordMessage}</Alert>}
        {passwordError && <Alert severity="error" sx={{ mb: 1 }}>{passwordError}</Alert>}
        <Stack spacing={2}>
          <TextField
            type="password"
            label="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
            autoComplete="current-password"
          />
          <TextField
            type="password"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            autoComplete="new-password"
            helperText={passwordPolicySummary()}
          />
          <TextField
            type="password"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            autoComplete="new-password"
          />
        </Stack>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={handleChangePassword}
          disabled={
            passwordSaving
            || !currentPassword
            || !validatePassword(newPassword).valid
            || newPassword !== confirmPassword
          }
        >
          {passwordSaving ? <CircularProgress size={22} /> : 'Update password'}
        </Button>
      </Box>
    </Stack>
  );
};
